import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../components/button';
import CustomModal from '../../components/CustomModal';
import Input from '../../components/input';
import Select from '../../components/Select';
import { createServiceIssue } from '../../store/Slices/ServiceSlice';
import { getUserDropdown } from '../../store/Slices/UserSlice';
import { priorityOptions } from '../../utils/Dropdowns';
import { formatUtcDateTimeForApi } from '../../utils/helper';
import { addIssueValidationSchema } from '../../utils/Schema';
import { closedIcon, inProgressIcon, openIcon, pendingIcon, resolvedIcon } from '../../utils/svgs';
import { errorNotify, successNotify } from '../../utils/toast';

const getNormalizedNumericValue = (value) => {
    if (value === undefined || value === null || value === '') {
        return null;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : null;
};

const getAssetTypeLabel = (assetType = 'vehicle') => {
    const normalizedAssetType = String(assetType || '').trim().toLowerCase();
    if (normalizedAssetType === 'equipment') return 'Equipment';
    if (normalizedAssetType === 'tyre') return 'Tyre';
    return 'Vehicle';
};

const getLastRecordedMeterValue = (detailData) => {
    const candidates = [
        detailData?.last_service?.odo_meter,
        detailData?.last_service?.usage_meter_reading,
        detailData?.odo_meter,
        detailData?.odm,
        detailData?.usage_meter_reading,
        detailData?.usageMeterReading,
        detailData?.meter_reading,
        detailData?.distance,
    ];

    for (const candidate of candidates) {
        if (candidate === undefined || candidate === null || candidate === '') {
            continue;
        }

        return candidate;
    }

    return 0;
};

const IssueTracking = ({
    vehicleDetailLoading,
    vehicleDetail,
    selectedVehicle,
    setLink_issue_ids,
    refreshVehicleDetail,
    setCheckedIssueIds,
    checkedIssueIds,
    setUpdateIssue,
    assetType = 'vehicle',
    assetId = null,
    assetLabel = '',
    serviceId = null,
    resolveDraftServiceId,
}) => {
    const dispatch = useDispatch();

    const userDetails = JSON.parse(localStorage.getItem("fms-maintenance-details"));

    const [issueFormOpen, setIssueFormOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('open');
    const displayAssetLabel = assetLabel || getAssetTypeLabel(assetType);
    const normalizedAssetType = String(assetType || 'vehicle').trim().toLowerCase();
    const lastRecordedMeterValue = getLastRecordedMeterValue(vehicleDetail);

    const { createIssueLoading } = useSelector((state) => state.services);
    const { userDropdownLoading, userDropdownData } = useSelector((state) => state.users)

    useEffect(() => {
        dispatch(getUserDropdown());
    }, [dispatch])

    useEffect(() => {
        setIssueFormOpen(false);
        setActiveTab('open');
    }, [assetType, assetId]);

    const getTabsData = () => {
        if (!vehicleDetail) {
            return [
                { name: "Open", key: "open", noOfIssues: 0, active: true },
                { name: "Resolved", key: "resolved", noOfIssues: 0, active: false },
                { name: "Closed", key: "closed", noOfIssues: 0, active: false },
            ];
        }

        return [
            {
                name: "Open",
                key: "open",
                noOfIssues: vehicleDetail.open_issues?.length || 0,
                active: activeTab === 'open'
            },
            {
                name: "Resolved",
                key: "resolved",
                noOfIssues: vehicleDetail.resolved_issues?.length || 0,
                active: activeTab === 'resolved'
            },
            {
                name: "Closed",
                key: "closed",
                noOfIssues: vehicleDetail.closed_issues?.length || 0,
                active: activeTab === 'closed'
            },
        ];
    };

    const getCurrentIssues = () => {
        if (!vehicleDetail) return [];

        switch (activeTab) {
            case 'open':
                return vehicleDetail.open_issues || [];
            case 'resolved':
                return vehicleDetail.resolved_issues || [];
            case 'closed':
                return vehicleDetail.closed_issues || [];
            default:
                return [];
        }
    };

    const handleTabChange = (tabKey) => {
        setActiveTab(tabKey);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString();
    };

    const userOptions = userDropdownData && userDropdownData.map(user => ({
        value: user.value,
        label: user.label,
        image: user.image,
        showIcon: true
    }));

    const initialValues = {
        asset_id: assetId ?? vehicleDetail?.asset_id ?? vehicleDetail?.id ?? '',
        service_id: serviceId ?? '',
        reported_by: userDetails?.id || '',
        assign_to: '',
        odo_meter: vehicleDetail?.odm ?? vehicleDetail?.odo_meter ?? vehicleDetail?.usage_meter_reading ?? '',
        priority: '',
        status: 'open',
        summery: '',
        description: '',
        reported_at: '',
        reported_time: '',
        tags: []
    };

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const resolvedAssetId = getNormalizedNumericValue(values.asset_id ?? assetId);
            if (resolvedAssetId === null) {
                throw new Error(`${displayAssetLabel} is required`);
            }

            const resolvedServiceId = getNormalizedNumericValue(serviceId)
                ?? getNormalizedNumericValue(values.service_id)
                ?? (typeof resolveDraftServiceId === 'function'
                    ? getNormalizedNumericValue(await resolveDraftServiceId())
                    : null);

            const issueData = {
                service_issue_id: '0',
                asset_type: normalizedAssetType,
                asset_id: resolvedAssetId,
                ...(resolvedServiceId !== null ? { service_id: resolvedServiceId } : {}),
                reported_by: parseInt(values.reported_by),
                assign_to: parseInt(values.assign_to),
                odo_meter: parseInt(values.odo_meter),
                priority: values.priority,
                status: values.status,
                summery: values.summery,
                description: values.description,
                reported_at: formatUtcDateTimeForApi(values.reported_at),
                tags: values.tags.map(tag => tag.value || tag)
            };

            await dispatch(createServiceIssue(issueData)).unwrap();
            successNotify('Issue created successfully');
            resetForm();
            setIssueFormOpen(false);

            // Use shared refresh function instead of direct API call
            if (refreshVehicleDetail) {
                await refreshVehicleDetail();
            }
        } catch (error) {
            errorNotify(error);
        } finally {
            setSubmitting(false);
        }
    };

    const addIssue = <CustomModal heading="Add Issue" size="lg" show={issueFormOpen} handleClose={() => setIssueFormOpen(false)}>
        <Formik
            initialValues={initialValues}
            validationSchema={addIssueValidationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
        >
            {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting, submitForm }) => (
                <div>
                    <h6>Details</h6>
                    <Row>
                        <Col md={12}>
                            <Input
                                label={displayAssetLabel}
                                placeholder={`Selected ${displayAssetLabel}`}
                                type="text"
                                name="asset_display"
                                value={selectedVehicle?.label || ''}
                                onChange={() => { }}
                                disabled={true}
                            />
                        </Col>
                        <Col md={12}>
                            <Select
                                label="Priority"
                                placeholder="Select Priority"
                                options={priorityOptions}
                                loading={false}
                                name="priority"
                                value={priorityOptions.find(option => option.value === values.priority)}
                                onChange={(selectedOption) => setFieldValue('priority', selectedOption?.value || '')}
                                onBlur={handleBlur}
                                error={errors.priority}
                                touched={touched.priority}
                                isSearch={false}
                            />
                        </Col>
                        <Col md={12}>
                            <Input
                                label="Reported Date & Time"
                                placeholder=""
                                type="datetime-local"
                                name="reported_at"
                                value={values.reported_at}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.reported_at}
                                touched={touched.reported_at}
                            />
                        </Col>
                        <Col md={12}>
                            <Input
                                label="Summary"
                                placeholder="Brief overview of the issue"
                                type="text"
                                name="summery"
                                value={values.summery}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.summery}
                                touched={touched.summery}
                            />
                        </Col>
                        <Col md={12}>
                            <Input
                                label="Description (Optional)"
                                placeholder="Add issue description"
                                type="text"
                                name="description"
                                value={values.description}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.description}
                                touched={touched.description}
                            />
                        </Col>
                        <Col md={12}>
                            <Select
                                label="Tags"
                                placeholder="Type and press Enter to create tags"
                                options={[]}
                                loading={false}
                                name="tags"
                                value={values.tags}
                                onChange={(selectedOptions) => setFieldValue('tags', selectedOptions || [])}
                                onBlur={handleBlur}
                                isMulti={true}
                                isCreatable={true}
                                error={errors.tags && typeof errors.tags === 'string' ? errors.tags : null}
                                touched={touched.tags}
                            />
                        </Col>
                        <Col md={12}>
                            <Input
                                label="Primary Meter"
                                placeholder="Enter Primary Meter Reading"
                                type="number"
                                name="odo_meter"
                                showExtraText={vehicleDetail ? true : false}
                                extraText={`Last Recorded: ${lastRecordedMeterValue || 0} KM`}
                                value={values.odo_meter}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.odo_meter}
                                touched={touched.odo_meter}
                            />
                        </Col>
                        <Col md={12}>
                            <Select
                                label="Reported By"
                                placeholder="Please Select"
                                options={userOptions}
                                loading={userDropdownLoading}
                                name="reported_by"
                                value={userOptions.find(option => option.value === values.reported_by)}
                                onChange={(selectedOption) => setFieldValue('reported_by', selectedOption?.value || '')}
                                onBlur={handleBlur}
                                error={errors.reported_by}
                                touched={touched.reported_by}
                                isSearch={false}
                            />
                        </Col>
                        <Col md={12}>
                            <Select
                                label="Assigned To"
                                placeholder="Please Select"
                                options={userOptions}
                                loading={userDropdownLoading}
                                name="assign_to"
                                value={userOptions.find(option => option.value === values.assign_to)}
                                onChange={(selectedOption) => setFieldValue('assign_to', selectedOption?.value || '')}
                                onBlur={handleBlur}
                                error={errors.assign_to}
                                touched={touched.assign_to}
                                isSearch={false}
                            />
                        </Col>
                        <Col md={12}>
                            <Button
                                cancelBtn={() => setIssueFormOpen(false)}
                                submitBtn={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    submitForm();
                                }}
                                loading={createIssueLoading}
                                disabled={createIssueLoading}
                                submitText="Add Issue"
                                type="button"
                            />
                        </Col>
                    </Row>
                </div>
            )}
        </Formik>
    </CustomModal>

    const tabs = getTabsData();
    const currentIssues = getCurrentIssues();

    useEffect(() => {
        if (currentIssues) {

            // const saved = localStorage.getItem(`checkedIssueIds_new`);

            // if (saved) {
            //     setCheckedIssueIds(JSON.parse(saved));
            //     return;
            // }

            let newArr = [];
            currentIssues?.forEach((issue) => {
                if (issue.is_service) {
                    newArr.push(issue.id);
                }
            });
            setCheckedIssueIds(prev => {
                if (JSON.stringify(prev) !== JSON.stringify(newArr)) {
                    return newArr;
                }
                return prev;
            });
        }
    }, [currentIssues]);

    const handleCheckboxChange = (e, id) => {
        const checked = e.target.checked;

        if (setUpdateIssue) {
            setUpdateIssue(true);
        }

        setCheckedIssueIds(prev => {
            const updatedCheckedIds = checked
                ? [...prev, id]
                : prev.filter(x => x !== id);

            setLink_issue_ids(updatedCheckedIds);

            return updatedCheckedIds;
        });
    };

    return (
        <div className='issue_tracking_main'>
            {addIssue}
            <h6>Issue Tracking</h6>

            {
                !vehicleDetail ?
                    <div className='no_vehicle_selected'>
                        {
                            vehicleDetailLoading ? <p>Loading...</p> :
                                <p>No {displayAssetLabel.toLowerCase()} selected. Please select a {displayAssetLabel.toLowerCase()} to track issues.</p>
                        }
                    </div> : <>
                        {
                            vehicleDetailLoading ? <div className='no_vehicle_selected'> <p>Loading...</p> </div> : <>
                                <div className='issue_tabs'>
                                    <Row>
                                        {
                                            tabs.map((tab, index) => (
                                                <Col key={index} md={4}>
                                                    <div
                                                        className={`issue_tab ${tab.active ? 'active' : ''}`}
                                                        onClick={() => handleTabChange(tab.key)}
                                                    >
                                                        {tab.name} ({tab.noOfIssues})
                                                    </div>
                                                </Col>
                                            ))
                                        }
                                    </Row>
                                </div>

                                <div className='issue_tracking_lists'>
                                    {
                                        currentIssues.length > 0 ? (
                                            currentIssues.map((issue) => {
                                                const id = issue.id;
                                                const checked = checkedIssueIds.includes(id);
                                                return (
                                                    <div key={issue.id} className='issue_tracking_item'>
                                                        <div>
                                                            <div>
                                                                {activeTab === 'open' && (
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={checked}
                                                                        onChange={(e) => handleCheckboxChange(e, id)}
                                                                        style={{ width: 16, height: 16, marginRight: 6, cursor: 'pointer', accentColor: "#4F19E2" }}
                                                                    />
                                                                )}
                                                            </div>
                                                            <div className='d-flex flex-column'>
                                                                {issue.summery || issue.description || 'No summary available'}
                                                                <span>
                                                                    Issue ID: {issue.service_issue_id} - {formatDate(issue.reported_at)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className={`d-flex align-items-center gap-1 status ${issue?.status}`} style={{ cursor: "pointer", textTransform: "capitalize" }}>
                                                            {issue?.status === "pending" ? pendingIcon : issue?.status === "open" ? openIcon : issue?.status === "closed" ? closedIcon : issue?.status === "resolved" ? resolvedIcon : issue?.status === "completed" ? resolvedIcon : issue?.status === "in_progress" ? inProgressIcon : null}
                                                            {issue?.status}
                                                        </div>
                                                    </div>
                                                )
                                            })
                                        ) : (
                                            <div className='no_issues'>
                                                <p>No {activeTab} issues found for this {displayAssetLabel.toLowerCase()}.</p>
                                            </div>
                                        )
                                    }
                                </div>
                            </>
                        }

                        <div>
                            <button
                                disabled={!vehicleDetail || vehicleDetailLoading}
                                className='add_issue_btn'
                                type='button'
                                onClick={() => setIssueFormOpen(true)}>+ Add Issue</button>
                        </div>
                    </>
            }

        </div>
    )
}

export default IssueTracking
